import { v4 as uuidv4 } from 'uuid';
import { responseError } from '@/utils/errors';
import findResult, {
  assignIfExists,
  mapResults,
  findWithReadStatus,
  removeItemByCode,
  assignValue,
  findSelectedInCategory,
  assignProps,
  hasKey,
  findResultInCategory,
  findSelected,
  selectableResultWithDates,
  findMatch,
  newName,
  previousCarouselitem,
} from '@/utils/resultsHelper';
import { primarySelectedDictionary } from '@/datasets/dictionaries/helper';
import withDraft from '@/utils/draftHelper';
import { findWithUUID } from '@/utils/uuidHelper';
import { saveStates, states } from '@/constants/lifecycle';
import { isCommonsModeEnabled } from '@/auth/featureToggles';
import pruneCohortErrors, {
  findAllCohorts, findCohort, findCohorts, uniqueDictionariesInClauses, assignEditCohortProps,
} from './shared';
import actions, { cohortLists, cohortLifeCycle } from '../constants';
import { newCohortClause, clauseNames } from '../clauses/state/state';
import newCohortQuery from '../queries/state/state';
import cohortProfile from '../profile/state/state';
import {
  newCohortProperties,
  newCohortState,
  cohortDatasets,
  cohortDictionaries,
  mergeCohort,
  openCohort,
  pruneCohortDictionaryErrors,
  cohortStateToLoad,
} from './state';
import validateQuery from '../queries/helper';
import { validateCohort } from '../helper';

const toggleCohortsSelected = withDraft((draft, action) => {
  const item = findCohorts(draft, action);
  item.results.forEach((result) => {
    assignValue(result, 'selected', action.selected);
  });
});

const updateCohortsFilter = withDraft((draft, action) => {
  const item = findCohorts(draft, action);
  item.filter = action.value;
});

const updateCohortsFlags = withDraft((draft, action) => {
  const item = findCohorts(draft, action);
  item.currentPage = 1;
  item.readStatus = states.PENDING;
  item.readErrors = [];
  item.privateFlag = action.privateFlag;
  item.internalFlag = action.internalFlag;
});

const fetchCohorts = withDraft((draft, action) => {
  const item = findCohorts(draft, action);
  item.readStatus = states.PENDING;
  item.readErrors = [];
});

const showCohorts = withDraft((draft, action) => {
  const item = findCohorts(draft, action);
  item.readStatus = states.SUCCESS;
  item.readErrors = [];
  item.results = action.append
    ? mapResults(item.results, action, selectableResultWithDates)
    : mapResults([], action, selectableResultWithDates);
  item.pageSize = action.pageSize;
  item.count = action.resultsCount;
  item.currentPage = action.currentPage;
  item.totalPages = action.totalPages;
});

const showCohortsError = withDraft((draft, action) => {
  const item = findCohorts(draft, action);
  item.readStatus = states.ERROR;
  item.readErrors = [responseError(action)];
});

const closeCohorts = withDraft((draft, action) => {
  draft.cohorts[action.category] = cohortStateToLoad({ cohorts: draft }).cohorts[action.category];
});

const showDeleteCohorts = withDraft((draft, action) => {
  draft.deleteCohortOpen = true;
  draft.cohorts.deletion.results = findSelectedInCategory(draft.cohorts, action.category);
});

const toggleCohortsPresentation = withDraft((draft, action) => {
  const item = findCohorts(draft, action);
  item.display = action.display;
  item.readStatus = states.PENDING;
  item.readErrors = [];
  item.results = [];
  item.count = 0;
  item.currentPage = 1;
  item.totalPages = 0;
});

const editCohortSuccess = withDraft((draft, { code, result }) => {
  findAllCohorts(draft, { items: [{ code }] }).forEach((category) => {
    const item = findResult(draft.cohorts, category, code);
    const queries = mergeCohort(result, item);
    if (hasKey(item, 'copy')) {
      item.copy = {};
    }
    assignIfExists(
      item,
      {
        ...result,
        queries,
        activeQueryUUID: queries ? queries[0].uuid : null,
        saveStatus: states.SUCCESS,
        saveErrors: [],
        isDirty: false,
        isEditing: false,
      },
    );
  });
});

const newCohort = withDraft((draft, action) => {
  const item = findCohort(draft, action);

  draft.activeCohort = { open: false };
  item.previousCode = item.code;
  item.copy = {};

  const queries = mergeCohort(action.result, item);
  assignIfExists(
    item,
    {
      ...action.result,
      queries,
      activeQueryUUID: queries[0].uuid,
      saveStatus: states.SUCCESS,
      saveErrors: [],
      isDirty: false,
      isEditing: false,
      isNew: false,
    },
  );
  findWithReadStatus(draft.cohorts, cohortLists, states.SUCCESS).forEach((category) => {
    draft.cohorts[category].readStatus = states.PENDING;
    draft.cohorts[category].readErrors = [];
  });
});

const editCohort = withDraft((draft, action) => {
  const item = findCohort(draft, action);

  assignEditCohortProps(item);
  assignProps(item, {
    saveErrors: validateCohort(item),
  });
});

const showSaveCohort = withDraft((draft, action) => {
  const item = findCohort(draft, action);
  assignProps(draft.activeCohort, newCohortProperties(item));
});

const updateCohortProperties = withDraft((draft, action) => {
  const item = findCohort(draft, action);

  assignEditCohortProps(item);
  assignIfExists(item, action.properties, cohortLifeCycle, true);
});

const toggleCohortSelected = withDraft((draft, action) => {
  const item = findCohort(draft, action);
  if (!item.selected && !draft.cohorts[action.category].multiSelect) {
    const selectedCohorts = findSelectedInCategory(draft.cohorts, action.category);
    selectedCohorts.forEach((cohort) => assignValue(cohort, 'selected', false));
  }
  item.selected = !item.selected;
});

const newCohortFromDataset = withDraft((draft, action) => {
  const item = findCohort(draft, action);

  if (item) {
    item.selectSource = false;
    item.datasets = cohortDatasets(true, true, isCommonsModeEnabled());
    item.dictionaries = cohortDictionaries();
  } else {
    draft.cohorts[action.category].results.push(newCohortState(action.code, false));
    draft.newCohortCode = uuidv4();
  }
});

const fetchCohort = withDraft((draft, action) => {
  const item = findCohort(draft, action);

  if (item) {
    item.readStatus = states.PENDING;
    item.readErrors = [];
  } else {
    const props = action.isNew ? newCohortState(action.code, true) : openCohort(action.code);
    draft.cohorts[action.category].results.push(props);
  }
});

const showCohort = withDraft((draft, action) => {
  const item = findCohort(draft, action);

  if (action.isNew) {
    assignProps(item, { ...action.result });
    draft.newCohortCode = uuidv4();
  } else {
    const errors = [];
    const queries = mergeCohort(action.result, item);
    queries.forEach((query) => validateQuery(query, errors, true));

    assignProps(item, {
      ...action.result,
      readStatus: states.SUCCESS,
      readErrors: [],
      validationErrors: errors,
      ...saveStates,
      queries,
      activeQueryUUID: queries.length ? queries[0].uuid : null,
      isDirty: false,
    });
  }
});

const showCohortError = withDraft((draft, action) => {
  const item = findCohort(draft, action);
  item.readStatus = states.ERROR;
  item.readErrors = [responseError(action)];
});

const showDeleteCohort = withDraft((draft, action) => {
  const item = findCohort(draft, action);
  draft.deleteCohortOpen = true;
  draft.cohorts.deletion.results = [item];
});

const closeCohort = withDraft((draft, action) => {
  const item = findCohort(draft, action);
  if (item?.code) {
    draft.cohorts[action.category].results = removeItemByCode(draft.cohorts[action.category].results, item);
  }
});

const updateCohortCancel = withDraft((draft, action) => {
  const item = findCohort(draft, action);
  const errors = [];
  item.queries.forEach((query) => validateQuery(query, errors, true));
  item.copy = {};
  assignIfExists(item, {
    ...saveStates,
    validationErrors: errors,
    isDirty: false,
    isEditing: false,
  });
});

const updateCohort = withDraft((draft, action) => {
  const item = findCohort(draft, action);
  item.saveStatus = states.PENDING;
  item.saveErrors = [];
});

const editCohortError = withDraft((draft, action) => {
  const item = findCohort(draft, action);
  assignIfExists(item, {
    saveStatus: states.ERROR,
    saveErrors: [responseError(action)],
  });
});

const addDictionaryToCohort = withDraft((draft, action) => {
  const item = findCohort(draft, action);
  assignEditCohortProps(item, true);
  const query = findWithUUID(item.queries, action.queryUUID);
  const dictionary = query.dataset.dictionaries.find((r) => (r.code === action.dictionaryCode));
  const name = newName('Clause', clauseNames(query));

  query.clauses.push(newCohortClause(dictionary, name));
  query.activeDictionaryCode = dictionary.code;
  if (!findMatch(query.profiles, dictionary.code)) {
    query.profiles.push(cohortProfile(dictionary.code));
  }
});

const removeDictionaryFromCohort = withDraft((draft, action) => {
  const item = findCohort(draft, action);
  assignEditCohortProps(item, true);
  const query = findWithUUID(item.queries, action.queryUUID);
  const dictionaries = uniqueDictionariesInClauses(query, true);
  const code = previousCarouselitem(dictionaries, action.dictionaryCode);
  if (!action.dictionaryCode) {
    query.clauses = query.clauses.filter((r) => (r.dictionary));
  } else { query.clauses = query.clauses.filter((r) => (r.dictionary?.code !== action.dictionaryCode)); }
  query.activeDictionaryCode = code === action.dictionaryCode ? null : code;
  item.saveErrors = pruneCohortDictionaryErrors(item.saveErrors, action);
});

const updateCohortPanels = withDraft((draft, action) => {
  const item = findCohort(draft, action);
  item.activeQueryUUID = action.queryUUID;
});

const datasetToCohort = withDraft((draft, action) => {
  const item = findCohort(draft, action);
  assignEditCohortProps(item);
  const dictionaries = action.result.dictionaries.filter((r) => (action.dictionaryCodes.includes(r.code)));
  item.queries = [newCohortQuery(action.result, dictionaries, 'Cohort', item)];
  item.settings = { ...action.result.settings };
  item.activeQueryUUID = item.queries[0].uuid;
  item.readStatus = states.SUCCESS;
  item.readErrors = [];
  item.selectSource = false;
});

const updateCohortDatasetsFlags = withDraft((draft, action) => {
  const item = findCohort(draft, action);
  item.datasets.readStatus = states.PENDING;
  item.datasets.readErrors = [];
  item.datasets.privateFlag = action.privateFlag;
  item.datasets.internalFlag = action.internalFlag;
  item.datasets.publicFlag = action.publicFlag;
  item.datasets.currentPage = 1;
});

const updateCohortDatasetsFilter = withDraft((draft, action) => {
  const item = findCohort(draft, action);
  item.datasets.filter = action.value;
});

const updateCohortDictionariesFilter = withDraft((draft, action) => {
  const item = findCohort(draft, action);
  item.dictionaries.filter = action.value;
});

const toggleCohortDatasetSelected = withDraft((draft, action) => {
  const item = findCohort(draft, action);
  const dataset = findResultInCategory(item.datasets, action.datasetCode);
  const selectedDatasets = findSelected(item.datasets.results);

  selectedDatasets.forEach((d) => assignValue(d, 'selected', false));
  dataset.selected = true;
  item.dictionaries.readStatus = states.PENDING;
  item.dictionaries.readErrors = [];
});

const toggleCohortDictionarySelected = withDraft((draft, action) => {
  const item = findCohort(draft, action);
  const dictionary = findResultInCategory(item.dictionaries, action.dictionaryCode);
  const selectedDictionaries = findSelected(item.dictionaries.results);

  selectedDictionaries.forEach((d) => assignValue(d, 'selected', false));
  dictionary.selected = !dictionary.selected;
});

const fetchCohortDictionaries = withDraft((draft, action) => {
  const item = findCohort(draft, action);
  item.dictionaries.readStatus = states.PENDING;
  item.dictionaries.readErrors = [];
});

const fetchCohortDatasets = withDraft((draft, action) => {
  const item = findCohort(draft, action);
  item.datasets.readStatus = states.PENDING;
  item.datasets.readErrors = [];
});

const showCohortDatasets = withDraft((draft, action) => {
  const item = findCohort(draft, action);
  item.datasets.readStatus = states.SUCCESS;
  item.datasets.readErrors = [];
  item.datasets.results = action.append
    ? mapResults(item.datasets.results, action, selectableResultWithDates)
    : mapResults([], action, selectableResultWithDates);
  item.datasets.pageSize = action.pageSize;
  item.datasets.count = action.resultsCount;
  item.datasets.currentPage = action.currentPage;
  item.datasets.totalPages = action.totalPages;
  item.dictionaries = cohortDictionaries();
});

const showCohortDictionaries = withDraft((draft, action) => {
  const item = findCohort(draft, action);
  item.dictionaries.readStatus = states.SUCCESS;
  item.dictionaries.readErrors = [];
  item.dictionaries.results = action.append
    ? mapResults(item.dictionaries.results, action, primarySelectedDictionary)
    : mapResults([], action, primarySelectedDictionary);
  item.dictionaries.pageSize = action.pageSize;
  item.dictionaries.count = action.resultsCount;
  item.dictionaries.currentPage = action.currentPage;
  item.dictionaries.totalPages = action.totalPages;
});

const toggleExpandedCharts = withDraft((draft, action) => {
  const item = findCohort(draft, action);
  item.expandedCharts = !item.expandedCharts;
});

const deleteCohort = withDraft((draft, action) => {
  findAllCohorts(draft, action).forEach((category) => {
    action.items.forEach(({ code }) => {
      const item = findResult(draft.cohorts, category, code);
      if (item) {
        item.destroyStatus = states.PENDING;
        item.destroyErrors = [];
      }
    });
  });
});

const destroyCohort = withDraft((draft, action) => {
  draft.deleteCohortOpen = false;
  findAllCohorts(draft, action).forEach((category) => {
    action.items.forEach(({ code }) => {
      const item = findResult(draft.cohorts, category, code);
      if (item) {
        draft.cohorts[category].results = removeItemByCode(draft.cohorts[category].results, item);
      }
    });
  });
});

const destroyCohortError = withDraft((draft, action) => {
  draft.deleteCohortOpen = false;
  findAllCohorts(draft, action).forEach((category) => {
    action.items.forEach(({ code, error }) => {
      const item = findResult(draft.cohorts, category, code);
      if (item) {
        item.destroyStatus = states.ERROR;
        item.destroyErrors = [error];
      }
    });
  });
});

const hideSaveCohort = withDraft((draft) => {
  draft.activeCohort = { open: false };
});

const hideDeleteCohort = withDraft((draft) => {
  draft.deleteCohortOpen = false;
  draft.cohorts.deletion.results = [];
});

const createCohort = withDraft((draft) => {
  draft.activeCohort.saveStatus = states.PENDING;
});

const updateActiveCohortProperties = withDraft((draft, action) => {
  assignIfExists(draft.activeCohort, action.properties);
  draft.activeCohort.saveErrors = pruneCohortErrors(draft.activeCohort.saveErrors, action);
});

const newCohortError = withDraft((draft, action) => {
  assignIfExists(draft.activeCohort, {
    saveStatus: states.ERROR,
    saveErrors: [responseError(action)],
  });
});

export default {
  [actions.ADD_DICTIONARY_TO_COHORT]: addDictionaryToCohort,
  [actions.CLOSE_COHORT]: closeCohort,
  [actions.CLOSE_COHORTS]: closeCohorts,
  [actions.CREATE_COHORT]: createCohort,
  [actions.DATASET_TO_COHORT]: datasetToCohort,
  [actions.DELETE_COHORT]: deleteCohort,
  [actions.DESTROY_COHORT]: destroyCohort,
  [actions.DESTROY_COHORT_ERROR]: destroyCohortError,
  [actions.EDIT_COHORT]: editCohort,
  [actions.EDIT_COHORT_ERROR]: editCohortError,
  [actions.EDIT_COHORT_SUCCESS]: editCohortSuccess,
  [actions.FETCH_COHORT]: fetchCohort,
  [actions.FETCH_COHORT_DATASETS]: fetchCohortDatasets,
  [actions.FETCH_COHORT_DICTIONARIES]: fetchCohortDictionaries,
  [actions.FETCH_COHORTS]: fetchCohorts,
  [actions.HIDE_DELETE_COHORT]: hideDeleteCohort,
  [actions.HIDE_SAVE_COHORT]: hideSaveCohort,
  [actions.NEW_COHORT]: newCohort,
  [actions.NEW_COHORT_ERROR]: newCohortError,
  [actions.NEW_COHORT_FROM_DATASET]: newCohortFromDataset,
  [actions.REFRESH_COHORTS]: fetchCohorts,
  [actions.REMOVE_DICTIONARY_FROM_COHORT]: removeDictionaryFromCohort,
  [actions.SHOW_COHORT]: showCohort,
  [actions.SHOW_COHORTS]: showCohorts,
  [actions.SHOW_COHORTS_ERROR]: showCohortsError,
  [actions.SHOW_COHORT_DATASETS]: showCohortDatasets,
  [actions.SHOW_COHORT_DICTIONARIES]: showCohortDictionaries,
  [actions.SHOW_COHORT_ERROR]: showCohortError,
  [actions.SHOW_DELETE_COHORT]: showDeleteCohort,
  [actions.SHOW_DELETE_COHORTS]: showDeleteCohorts,
  [actions.SHOW_SAVE_COHORT]: showSaveCohort,
  [actions.TOGGLE_COHORTS_PRESENTATION]: toggleCohortsPresentation,
  [actions.TOGGLE_COHORTS_SELECTED]: toggleCohortsSelected,
  [actions.TOGGLE_COHORT_DATASET_SELECTED]: toggleCohortDatasetSelected,
  [actions.TOGGLE_COHORT_DICTIONARY_SELECTED]: toggleCohortDictionarySelected,
  [actions.TOGGLE_COHORT_SELECTED]: toggleCohortSelected,
  [actions.TOGGLE_EXPANDED_CHARTS]: toggleExpandedCharts,
  [actions.UPDATE_ACTIVE_COHORT_PROPERTIES]: updateActiveCohortProperties,
  [actions.UPDATE_COHORT]: updateCohort,
  [actions.UPDATE_COHORTS_FILTER]: updateCohortsFilter,
  [actions.UPDATE_COHORTS_FLAGS]: updateCohortsFlags,
  [actions.UPDATE_COHORT_CANCEL]: updateCohortCancel,
  [actions.UPDATE_COHORT_DATASETS_FILTER]: updateCohortDatasetsFilter,
  [actions.UPDATE_COHORT_DATASETS_FLAGS]: updateCohortDatasetsFlags,
  [actions.UPDATE_COHORT_DICTIONARIES_FILTER]: updateCohortDictionariesFilter,
  [actions.UPDATE_COHORT_PANELS]: updateCohortPanels,
  [actions.UPDATE_COHORT_PROPERTIES]: updateCohortProperties,
};
